import React, { useState, useEffect } from 'react';
import { adminApiClient } from '../lib/api';
import {
  Building2,
  Users,
  UserCircle,
  Shield,
  Flame,
  Calendar,
  CalendarDays,
  Gift,
  CreditCard,
  DollarSign,
  Activity,
  AlertCircle,
  ChevronRight,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

interface DashboardProps {
  onNavigate: (tab: string) => void;
}

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b'];

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  onClick?: () => void;
}> = ({ title, value, icon: Icon, color, bgColor, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md hover:border-gray-200 transition-all group"
  >
    <div className="flex items-start justify-between">
      <div className={`p-2.5 rounded-lg ${bgColor} ${color}`}>
        <Icon size={22} />
      </div>
      {onClick && (
        <ChevronRight
          size={18}
          className="text-gray-300 group-hover:text-[var(--color-primary)] transition-colors"
        />
      )}
    </div>
    <p className="text-2xl font-bold text-gray-900 mt-3">{value}</p>
    <p className="text-sm text-gray-500 mt-0.5">{title}</p>
  </button>
);

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await adminApiClient.getDashboardStats();
      if (res.success && res.data?.stats) {
        setStats(res.data.stats);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Please log in to view dashboard data');
      } else {
        setError('Error loading dashboard. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary)] mb-4" />
        <p className="text-gray-500 font-medium">Loading Dashboard...</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="bg-red-50 p-4 rounded-full mb-4">
          <Activity className="h-10 w-10 text-red-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-gray-500 mb-6 max-w-md">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
        >
          <RefreshCw size={18} /> Retry
        </button>
      </div>
    );
  }

  if (!stats?.overview) return null;

  const ov = stats.overview;
  const rev = stats.revenue || {};
  const booking = stats.booking || {};
  const recentActivities = stats.recentActivities || [];
  const pendingActions = stats.pendingActions || [];

  const summaryCards = [
    {
      title: 'Total Temples',
      value: ov.totalTemples ?? 0,
      icon: Building2,
      color: 'text-[var(--color-secondary)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--color-secondary)_12%,white)]',
      target: 'temple-registrations',
    },
    {
      title: 'Temple Admins',
      value: ov.totalTempleAdmins ?? 0,
      icon: Shield,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      target: 'users-temple-admins',
    },
    {
      title: 'Priests',
      value: ov.totalPriests ?? 0,
      icon: UserCircle,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      target: 'users-priests',
    },
    {
      title: 'Devotees',
      value: ov.totalDevotees ?? 0,
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      target: 'users-devotees',
    },
    {
      title: 'Poojas',
      value: ov.totalPoojas ?? 0,
      icon: Flame,
      color: 'text-[var(--color-tertiary)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--color-tertiary)_12%,white)]',
      target: 'poojas',
    },
    {
      title: 'Pooja Bookings',
      value: ov.totalPoojaBookings ?? 0,
      icon: Calendar,
      color: 'text-[var(--color-primary)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--color-primary)_12%,white)]',
      target: 'pooja-bookings',
    },
    {
      title: 'Events',
      value: ov.totalEvents ?? 0,
      icon: CalendarDays,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      target: 'events',
    },
    {
      title: 'Event Bookings',
      value: ov.totalEventBookings ?? 0,
      icon: Calendar,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      target: 'event-bookings',
    },
    {
      title: 'Sponsorships',
      value: ov.totalSponsorships ?? 0,
      icon: Gift,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      target: 'sponsorships',
    },
    {
      title: 'Sponsorship Payments',
      value: ov.totalSponsorshipBookings ?? 0,
      icon: CreditCard,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      target: 'sponsorship-bookings',
    },
    {
      title: 'Total Revenue',
      value: `$${(ov.totalRevenue ?? 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      target: 'donations',
    },
    {
      title: 'Pending Approvals',
      value: ov.pendingApprovals ?? 0,
      icon: AlertCircle,
      color: 'text-[var(--color-tertiary)]',
      bgColor: 'bg-[color-mix(in_srgb,var(--color-tertiary)_12%,white)]',
      target: 'temple-registrations',
    },
    {
      title: 'Pending Verifications',
      value: ov.pendingVerifications ?? 0,
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      target: 'temple-registrations',
    },
  ];

  const revenuePieData = [
    { name: 'Donations', value: rev.donations || 0, color: COLORS[0] },
    { name: 'Pooja Bookings', value: rev.byPooja || 0, color: COLORS[1] },
    { name: 'Events', value: rev.byEvents || 0, color: COLORS[2] },
    { name: 'Sponsorships', value: rev.bySponsorships || 0, color: COLORS[3] },
  ].filter((d) => d.value > 0);

  const bookingBarData = [
    {
      name: 'Pooja',
      total: booking.poojaBookings?.total || 0,
      confirmed: booking.poojaBookings?.confirmed || 0,
      pending: booking.poojaBookings?.pending || 0,
      completed: booking.poojaBookings?.completed || 0,
      cancelled: booking.poojaBookings?.cancelled || 0,
    },
    {
      name: 'Event',
      total: booking.eventBookings?.total || 0,
      confirmed: booking.eventBookings?.confirmed || 0,
      pending: booking.eventBookings?.pending || 0,
      completed: booking.eventBookings?.completed || 0,
      cancelled: booking.eventBookings?.cancelled || 0,
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'donation':
        return <DollarSign size={14} className="text-green-600" />;
      case 'user':
        return <Users size={14} className="text-purple-600" />;
      case 'temple':
        return <Building2 size={14} className="text-[var(--color-secondary)]" />;
      case 'pooja_booking':
        return <Calendar size={14} className="text-[var(--color-primary)]" />;
      case 'event_booking':
        return <CalendarDays size={14} className="text-teal-600" />;
      case 'sponsorship':
        return <Gift size={14} className="text-rose-600" />;
      case 'temple_registration':
        return <Building2 size={14} className="text-indigo-600" />;
      default:
        return <Activity size={14} className="text-gray-500" />;
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-500 mt-1">
            Platform statistics and quick access to key modules
          </p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Statistics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {summaryCards.map((card, i) => (
            <StatCard
              key={i}
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
              bgColor={card.bgColor}
              onClick={() => onNavigate(card.target)}
            />
          ))}
        </div>
      </div>

      {/* Booking & Revenue Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Booking Summary */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp size={20} className="text-[var(--color-primary)]" />
            Booking Summary
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bookingBarData} layout="vertical" margin={{ left: 60, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={50} />
                <Tooltip />
                <Legend />
                <Bar dataKey="confirmed" name="Confirmed" fill="#10b981" stackId="a" />
                <Bar dataKey="pending" name="Pending" fill="#f59e0b" stackId="a" />
                <Bar dataKey="completed" name="Completed" fill="#3b82f6" stackId="a" />
                <Bar dataKey="cancelled" name="Cancelled" fill="#ef4444" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Summary */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <DollarSign size={20} className="text-green-500" />
            Revenue Summary
          </h3>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total Revenue</span>
              <span className="font-bold text-lg text-gray-900">
                ${(rev.total ?? 0).toLocaleString()}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-[color-mix(in_srgb,var(--color-secondary)_12%,white)] rounded-lg">
                <p className="text-xs text-[var(--color-secondary)] font-medium">Online</p>
                <p className="font-semibold text-[color-mix(in_srgb,var(--color-secondary)_85%,black)]">${(rev.online ?? 0).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-[color-mix(in_srgb,var(--color-tertiary)_12%,white)] rounded-lg">
                <p className="text-xs text-[var(--color-tertiary)] font-medium">Offline</p>
                <p className="font-semibold text-[color-mix(in_srgb,var(--color-tertiary)_85%,black)]">${(rev.offline ?? 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
          {revenuePieData.length > 0 && (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={revenuePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                    }
                  >
                    {revenuePieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${Number(value ?? 0).toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activities & Pending Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activities */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Activity size={20} className="text-[var(--color-secondary)]" />
            Recent Activity
          </h3>
          <div className="space-y-4 max-h-[360px] overflow-y-auto pr-2">
            {recentActivities.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            ) : (
              recentActivities.map((a: any) => (
                <div
                  key={a.id}
                  className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    {getActivityIcon(a.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">{a.message}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{a.time}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Actions */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <AlertCircle size={20} className="text-[var(--color-tertiary)]" />
            Pending Actions
          </h3>
          <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2">
            {pendingActions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No pending actions</p>
            ) : (
              pendingActions.map((p: any) => (
                <button
                  key={p.id}
                  onClick={() => onNavigate(p.link || 'temple-registrations')}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-[color-mix(in_srgb,var(--color-tertiary)_24%,white)] bg-[color-mix(in_srgb,var(--color-tertiary)_10%,white)] hover:bg-[color-mix(in_srgb,var(--color-tertiary)_15%,white)] transition-colors text-left group"
                >
                  <div>
                    <p className="font-medium text-gray-900">{p.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {p.date
                        ? new Date(p.date).toLocaleDateString()
                        : 'Awaiting review'}
                    </p>
                  </div>
                  <ChevronRight
                    size={18}
                    className="text-gray-400 group-hover:text-[var(--color-primary)] transition-colors"
                  />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
